import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, IconButton, CircularProgress, Stack, Tooltip,
  Dialog, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ChecklistIcon from '@mui/icons-material/Checklist';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Link as RouterLink } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/de';
import { useTranslation } from 'react-i18next';
import { useOffenePunkte, useCreateOffenerPunkt, useUpdateOffenerPunkt, useDeleteOffenerPunkt } from '../../../api/hooks/useOffenePunkte';
import { useMeetings } from '../../../api/hooks/useMeetings';
import { useArbeitspakete, useCreateArbeitspaket } from '../../../api/hooks/useArbeitspakete';
import { useAnforderungen, useCreateAnforderung } from '../../../api/hooks/useAnforderungen';
import { useCreateRisiko } from '../../../api/hooks/useRisiken';
import { useProject } from '../../../api/hooks/useProjects';
import { useSyncMaturityFromCount } from '../../../api/hooks/useReifegrad';
import { useNotifications } from '../../../context/NotificationContext';
import { useAuth } from '../../../context/AuthContext';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { PmCard, PmInfoBox, PmPill, PmToolbar, PmViewToggle, PmRichTextEditor, RichTextView } from '../../../components/pm';
import { AttachmentList } from '../../../components/common/AttachmentList';
import { PdfExportButton } from '../../../components/common/PdfExportButton';
import { buildOffenePunkteReport } from '../reports/offenePunkteReport';
import type { ReportMeta } from '../../../lib/pdf/reportModel';
import { projectKuerzel, nextTopLevelNr, type ApNumberNode } from '../lib/apNumbering';
import { apiErrorMessage } from '../../../lib/apiErrorMessage';
import { AppDialogTitle } from '../../../components/common/AppDialogTitle';

interface OffenerPunkt {
  id: string;
  titel: string | null;
  kategorie: string | null;
  faellig: string | null;
  status: 'Offen' | 'In Klärung' | 'Beantwortet' | 'Geschlossen';
  beschreibung: string | null;
  verantwortlich: string | null;
  meeting_id: string | null;
  quelle: string | null;
}

export const KATEGORIEN = ['Allgemein', 'Risiko', 'Anforderung', 'Arbeitspaket', 'Meilenstein'] as const;
const STATUSES = ['Offen', 'In Klärung', 'Beantwortet', 'Geschlossen'] as const;

const KAT_TONE: Record<string, 'k09' | 'k02' | 'k11' | 'k01' | 'neutral'> = {
  'Risiko': 'k09',
  'Anforderung': 'k02',
  'Arbeitspaket': 'k11',
  'Meilenstein': 'k01',
  'Allgemein': 'neutral',
};

const KAT_ACCENT: Record<string, string> = {
  'Risiko': 'var(--pm-error)',
  'Anforderung': 'var(--pm-info)',
  'Arbeitspaket': 'var(--pm-warning)',
  'Meilenstein': 'var(--pm-success)',
  'Allgemein': 'var(--pm-gray-300)',
};

const STATUS_TONE: Record<OffenerPunkt['status'], 'neutral' | 'info' | 'warning' | 'success'> = {
  'Offen': 'neutral',
  'In Klärung': 'info',
  'Beantwortet': 'warning',
  'Geschlossen': 'success',
};

type Op = OffenerPunkt;
type ConvertTarget = 'ap' | 'anforderung' | 'risiko';

/** HTML der Beschreibung -> Klartext (Anforderung/Risiko speichern Plaintext). */
export function htmlToPlain(html: string | null): string {
  if (!html) return '';
  const withBreaks = html.replace(/<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi, '\n');
  const text = new DOMParser().parseFromString(withBreaks, 'text/html').body.textContent ?? '';
  return text.replace(/\n{3,}/g, '\n\n').trim();
}
type ViewMode = 'notes' | 'kanban';

interface FormState {
  titel: string;
  kategorie: string;
  faellig: Dayjs | null;
  status: OffenerPunkt['status'];
  beschreibung: string;
  verantwortlich: string;
}

const INITIAL_FORM: FormState = { titel: '', kategorie: '', faellig: null, status: 'Offen', beschreibung: '', verantwortlich: '' };

function NoteCard({
  op,
  herkunft,
  onEdit,
  onDelete,
  onConvert,
}: {
  op: Op;
  herkunft: React.ReactNode;
  onEdit: (op: Op) => void;
  onDelete: (id: string) => void;
  onConvert: (op: Op, target: ConvertTarget) => void;
}) {
  const { t } = useTranslation('projects');
  const accent = KAT_ACCENT[op.kategorie ?? 'Allgemein'];
  return (
    <PmCard
      sx={{
        position: 'relative',
        borderTop: `4px solid ${accent}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minHeight: 140,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1 }}>
        <PmPill tone={KAT_TONE[op.kategorie ?? 'Allgemein'] ?? 'neutral'}>
          {op.kategorie ?? 'Allgemein'}
        </PmPill>
        <PmPill tone={STATUS_TONE[op.status]}>{op.status}</PmPill>
      </Stack>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--pm-fg-base)' }}>
        {op.titel ?? '—'}
      </Typography>
      <RichTextView html={op.beschreibung} sx={{ flex: 1, fontSize: 13 }} />
      {herkunft && (
        <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', fontStyle: 'italic' }}>
          {herkunft}
        </Typography>
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 'auto' }}>
        <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
          {op.faellig ? `Fällig: ${dayjs(op.faellig).format('DD.MM.YYYY')}` : '—'}
        </Typography>
        <Box>
          <Tooltip title={t('offenePunkte.convertToAp', 'In Arbeitspaket umwandeln')}>
            <IconButton size="small" onClick={() => onConvert(op, 'ap')} sx={{ color: 'var(--pm-fg-subtle)' }}>
              <AssignmentTurnedInIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('offenePunkte.convertToAnforderung', 'Als Anforderung übernehmen')}>
            <IconButton size="small" aria-label={t('offenePunkte.convertToAnforderung', 'Als Anforderung übernehmen')} onClick={() => onConvert(op, 'anforderung')} sx={{ color: 'var(--pm-fg-subtle)' }}>
              <ChecklistIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('offenePunkte.convertToRisiko', 'Als Risiko übernehmen')}>
            <IconButton size="small" aria-label={t('offenePunkte.convertToRisiko', 'Als Risiko übernehmen')} onClick={() => onConvert(op, 'risiko')} sx={{ color: 'var(--pm-fg-subtle)' }}>
              <WarningAmberIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={() => onEdit(op)} sx={{ color: 'var(--pm-fg-subtle)' }}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(op.id)} sx={{ color: 'var(--pm-error)' }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Stack>
    </PmCard>
  );
}

export function OffenePunkteTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();
  const { user } = useAuth();
  const currentUserName = user?.name?.trim() ?? '';

  const { data, isLoading } = useOffenePunkte(projectId);
  const { data: meetingsResp } = useMeetings(projectId);
  const { data: projectResp } = useProject(projectId);
  const createMut = useCreateOffenerPunkt(projectId);
  const updateMut = useUpdateOffenerPunkt(projectId);
  const deleteMut = useDeleteOffenerPunkt(projectId);
  const items = ((data as unknown as { data?: Op[] } | undefined)?.data ?? []);
  const meetings = ((meetingsResp as unknown as { data?: { id: string; titel: string | null; datum?: string | null }[] } | undefined)?.data ?? []);
  const projectName = (projectResp as unknown as { data?: { name?: string } } | undefined)?.data?.name ?? 'Projekt';
  const projectNickname = (projectResp as unknown as { data?: { nickname?: string | null } } | undefined)?.data?.nickname ?? null;
  useSyncMaturityFromCount(projectId, 13, isLoading ? -1 : items.length);

  // P-12: OP -> AP umwandeln. Nutzt denselben AP-Create-Pfad + Auto-Nummerierung
  // wie die Arbeitspakete-Liste; die Herkunft (quelle_typ/quelle_id) verweist auf
  // den urspruenglichen offenen Punkt, der dabei unveraendert bestehen bleibt.
  const { data: apResp } = useArbeitspakete(projectId);
  const createApMut = useCreateArbeitspaket(projectId);
  const apItems = ((apResp as unknown as { data?: { id: string; nr: string; parent_id: string | null }[] } | undefined)?.data ?? []);
  const apNodes = useMemo<ApNumberNode[]>(() => apItems.map((a) => ({ id: a.id, nr: a.nr, parent_id: a.parent_id })), [apItems]);
  const kuerzel = projectKuerzel(projectNickname, projectName);
  const [convert, setConvert] = useState<{ op: Op; target: ConvertTarget } | null>(null);
  const convertOp = convert?.op ?? null;

  // PHQ-88: OP zusaetzlich als Anforderung bzw. Risiko uebernehmen (Kopie, OP bleibt bestehen).
  const { data: anfResp } = useAnforderungen(projectId);
  const createAnfMut = useCreateAnforderung(projectId);
  const createRisikoMut = useCreateRisiko(projectId);
  const anfCount = ((anfResp as unknown as { data?: unknown[] } | undefined)?.data ?? []).length;

  // Herkunft eines OP: aus welchem Meeting (mit Datum, verlinkt) bzw. von welcher Seite erfasst.
  const herkunftOf = (op: Op): React.ReactNode => {
    if (op.meeting_id) {
      const m = meetings.find((mm) => mm.id === op.meeting_id);
      const titel = m?.titel?.trim();
      const datum = m?.datum ? dayjs(m.datum).format('DD.MM.YYYY') : null;
      return (
        <>
          {t('offenePunkte.herkunftMeetingPre', 'Erfasst in Meeting')}{' '}
          <RouterLink to={`/projects/${projectId}/meetings`} style={{ color: 'inherit' }}>
            {titel || t('offenePunkte.herkunftMeetingOhneTitel', '(ohne Titel)')}
          </RouterLink>
          {datum && `, ${t('offenePunkte.herkunftAm', 'am {{datum}}', { datum })}`}
        </>
      );
    }
    if (op.quelle && op.quelle !== 'offene-punkte') {
      return t('offenePunkte.herkunftQuelle', 'Erfasst auf: {{quelle}}', { quelle: op.quelle });
    }
    return t('offenePunkte.herkunftSeite', 'Erfasst auf der Offene-Punkte-Seite');
  };

  const [view, setView] = useState<ViewMode>('notes');
  const [search, setSearch] = useState('');
  const [filterKategorie, setFilterKategorie] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [onlyMine, setOnlyMine] = useState(false);

  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState<Op | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);

  useEffect(() => {
    if (!open) return;
    if (editItem) {
      setForm({
        titel: editItem.titel ?? '',
        kategorie: editItem.kategorie ?? '',
        faellig: editItem.faellig ? dayjs(editItem.faellig) : null,
        status: editItem.status,
        beschreibung: editItem.beschreibung ?? '',
        verantwortlich: editItem.verantwortlich ?? '',
      });
    } else {
      setForm(INITIAL_FORM);
    }
  }, [open, editItem]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const mineNeedle = currentUserName.toLowerCase();
    return items.filter((op) => {
      if (filterKategorie && op.kategorie !== filterKategorie) return false;
      if (filterStatus && op.status !== filterStatus) return false;
      if (onlyMine && mineNeedle) {
        const verantwortlich = (op.verantwortlich ?? '').trim().toLowerCase();
        if (!verantwortlich.includes(mineNeedle)) return false;
      }
      if (q) {
        const plain = (op.beschreibung ?? '').replace(/<[^>]*>/g, ' ');
        const hay = `${op.titel ?? ''} ${plain}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, filterKategorie, filterStatus, onlyMine, currentUserName]);

  const grouped = useMemo(() => {
    const map: Record<OffenerPunkt['status'], Op[]> = {
      'Offen': [], 'In Klärung': [], 'Beantwortet': [], 'Geschlossen': [],
    };
    filtered.forEach((op) => { map[op.status].push(op); });
    return map;
  }, [filtered]);

  const handleSave = async () => {
    const payload: Record<string, unknown> = {
      titel: form.titel.trim() || null,
      kategorie: form.kategorie || null,
      faellig: form.faellig?.format('YYYY-MM-DD') ?? null,
      status: form.status,
      beschreibung: form.beschreibung.trim() || null,
      verantwortlich: form.verantwortlich.trim() || null,
    };
    try {
      if (editItem) {
        // meeting_id/quelle bewusst nicht mitsenden -> Herkunft bleibt erhalten.
        await updateMut.mutateAsync({ id: editItem.id, data: payload });
      } else {
        await createMut.mutateAsync({ ...payload, meeting_id: null, quelle: 'offene-punkte' });
      }
      notify(tc('status.saved', 'Gespeichert'), 'success');
      setOpen(false); setEditItem(null);
    } catch (err) {
      notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await deleteMut.mutateAsync(deleteId); notify(tc('status.deleted', 'Gelöscht'), 'success'); }
    catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
    finally { setDeleteId(null); }
  };

  const handleConvertAnforderungRisiko = async (op: Op, target: 'anforderung' | 'risiko') => {
    const titel = op.titel?.trim() ?? '';
    const plain = htmlToPlain(op.beschreibung);
    const text = [titel, plain].filter(Boolean).join('\n\n');
    try {
      if (target === 'anforderung') {
        await createAnfMut.mutateAsync({
          nr: String(anfCount + 1), role: null, ziel: text, nutzen: null,
          kategorie: null, story_points: null, status: 'Neu',
        });
        notify(t('offenePunkte.convertedAnforderung', 'Anforderung angelegt — im Reiter Anforderungen zu finden.'), 'success');
      } else {
        await createRisikoMut.mutateAsync({
          beschreibung: text, risiko: null, umgebung: null, weight: 3, assessment: 3,
          strategie: null, action: null, status: 'Identifiziert',
        });
        notify(t('offenePunkte.convertedRisiko', 'Risiko angelegt — im Reiter Risiken zu finden.'), 'success');
      }
    } catch (err) {
      notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error');
    } finally {
      setConvert(null);
    }
  };

  const handleConvert = async () => {
    if (!convert) return;
    if (convert.target !== 'ap') { await handleConvertAnforderungRisiko(convert.op, convert.target); return; }
    const convertOp = convert.op;
    const nr = nextTopLevelNr(kuerzel, apNodes);
    try {
      await createApMut.mutateAsync({
        nr,
        titel: convertOp.titel?.trim() || nr,
        beschreibung: convertOp.beschreibung ?? null,
        parent_id: null,
        quelle_typ: 'offener_punkt',
        quelle_id: convertOp.id,
      });
      notify(
        t('offenePunkte.convertedSuccess', 'Arbeitspaket „{{nr}}" angelegt — im Reiter Arbeitspakete zu finden.', { nr }),
        'success',
      );
    } catch (err) {
      notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error');
    } finally {
      setConvert(null);
    }
  };

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  const openEdit = (op: Op) => { setEditItem(op); setOpen(true); };

  const pdfMeta: ReportMeta = { projectName, pageTitle: t('offenePunkte.title', 'Offene Punkte') };
  const buildReport = () => buildOffenePunkteReport({ items });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="h6">{t('offenePunkte.title', 'Offene Punkte')}</Typography>
          <Stack direction="row" gap={1.5} alignItems="center">
            <PdfExportButton meta={pdfMeta} build={buildReport} />
            <PmViewToggle<ViewMode>
              value={view}
              onChange={setView}
              options={[
                { value: 'notes', label: t('offenePunkte.viewNotes', 'Notizen'), icon: <ViewModuleIcon fontSize="small" /> },
                { value: 'kanban', label: t('offenePunkte.viewKanban', 'Kanban'), icon: <ViewKanbanIcon fontSize="small" /> },
              ]}
            />
          </Stack>
        </Stack>

        <OpQuickAdd
          onCreate={async (titel, kategorie) => {
            await createMut.mutateAsync({
              titel: titel.trim(), kategorie: kategorie || null, faellig: null,
              status: 'Offen', beschreibung: null, verantwortlich: null,
              meeting_id: null, quelle: 'offene-punkte',
            });
          }}
        />

        <PmToolbar
          search={{ value: search, placeholder: t('offenePunkte.search', 'Suchen ...'), onChange: setSearch }}
          filters={[
            {
              key: 'kategorie',
              label: t('offenePunkte.kategorie', 'Kategorie'),
              value: filterKategorie,
              options: [{ value: '', label: t('offenePunkte.allKategorien', 'Alle Kategorien') }, ...KATEGORIEN.map((k) => ({ value: k, label: k }))],
            },
            {
              key: 'status',
              label: t('fields.state', 'Status'),
              value: filterStatus,
              options: [{ value: '', label: t('offenePunkte.allStatuses', 'Alle Status') }, ...STATUSES.map((s) => ({ value: s, label: s }))],
            },
          ]}
          onFilterChange={(k, v) => {
            if (k === 'kategorie') setFilterKategorie(v);
            else if (k === 'status') setFilterStatus(v);
          }}
          actions={currentUserName ? (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={onlyMine}
                  onChange={(e) => setOnlyMine(e.target.checked)}
                />
              }
              label={t('offenePunkte.onlyMine', 'Nur meine')}
              sx={{ mr: 0, whiteSpace: 'nowrap', color: 'var(--pm-fg-muted)' }}
            />
          ) : undefined}
        />

        {filtered.length === 0 ? (
          <PmInfoBox variant="info" title={t('offenePunkte.empty', 'Keine offenen Punkte')}>
            {items.length === 0
              ? t('offenePunkte.emptyHint', 'Lege den ersten Eintrag an um zu starten.')
              : t('offenePunkte.filterEmpty', 'Keine Treffer fuer die aktuelle Filter-Kombination.')}
          </PmInfoBox>
        ) : view === 'notes' ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' },
              gap: 2,
            }}
          >
            {filtered.map((op) => (
              <NoteCard key={op.id} op={op} herkunft={herkunftOf(op)} onEdit={openEdit} onDelete={(id) => setDeleteId(id)} onConvert={(o, target) => setConvert({ op: o, target })} />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
              gap: 1.5,
              alignItems: 'flex-start',
            }}
          >
            {STATUSES.map((status) => (
              <PmCard
                key={status}
                sx={{
                  borderTop: `4px solid ${
                    status === 'Offen' ? 'var(--pm-gray-400)' :
                    status === 'In Klärung' ? 'var(--pm-info)' :
                    status === 'Beantwortet' ? 'var(--pm-warning)' :
                    'var(--pm-success)'
                  }`,
                  background: 'var(--pm-bg-deep)',
                  minHeight: 240,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="overline" sx={{ color: 'var(--pm-fg-muted)', fontWeight: 600 }}>
                    {status}
                  </Typography>
                  <PmPill tone="neutral">{grouped[status].length}</PmPill>
                </Stack>
                <Stack spacing={1}>
                  {grouped[status].length === 0 && (
                    <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', textAlign: 'center', py: 2 }}>
                      —
                    </Typography>
                  )}
                  {grouped[status].map((op) => (
                    <NoteCard key={op.id} op={op} herkunft={herkunftOf(op)} onEdit={openEdit} onDelete={(id) => setDeleteId(id)} onConvert={(o, target) => setConvert({ op: o, target })} />
                  ))}
                </Stack>
              </PmCard>
            ))}
          </Box>
        )}

        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
          <AppDialogTitle>{editItem ? t('offenePunkte.editTitle', 'Eintrag bearbeiten') : t('offenePunkte.createTitle', 'Neuer Eintrag')}</AppDialogTitle>
          <DialogContent>
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <TextField label={t('offenePunkte.titel', 'Titel')} value={form.titel} onChange={(e) => setForm({ ...form, titel: e.target.value })} fullWidth />
              <FormControl fullWidth>
                <InputLabel>{t('offenePunkte.kategorie', 'Kategorie')}</InputLabel>
                <Select value={form.kategorie} label={t('offenePunkte.kategorie', 'Kategorie')} onChange={(e) => setForm({ ...form, kategorie: e.target.value })}>
                  <MenuItem value="">—</MenuItem>
                  {KATEGORIEN.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField
                label={t('offenePunkte.verantwortlich', 'Verantwortlich')}
                value={form.verantwortlich}
                onChange={(e) => setForm({ ...form, verantwortlich: e.target.value })}
                placeholder="z.B. M. Müller"
                fullWidth
              />
              <DatePicker label={t('offenePunkte.faellig', 'Fällig')} value={form.faellig} onChange={(v) => setForm({ ...form, faellig: v })} slotProps={{ textField: { fullWidth: true } }} />
              <FormControl fullWidth>
                <InputLabel>{t('fields.state', 'Status')}</InputLabel>
                <Select value={form.status} label={t('fields.state', 'Status')} onChange={(e) => setForm({ ...form, status: e.target.value as OffenerPunkt['status'] })}>
                  {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              <Box>
                <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)', display: 'block', mb: 0.5 }}>{t('fields.description', 'Beschreibung')}</Typography>
                <PmRichTextEditor
                  value={form.beschreibung}
                  onChange={(html) => setForm({ ...form, beschreibung: html })}
                  placeholder={t('offenePunkte.beschreibungPlaceholder', 'Beschreibung — Formatierung, Listen, Emojis …')}
                />
              </Box>
              <Box>
                <AttachmentList projectId={projectId} entityType="offener_punkt" entityId={editItem?.id} />
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>{tc('actions.cancel', 'Abbrechen')}</Button>
            <Button variant="contained" onClick={handleSave} disabled={createMut.isPending || updateMut.isPending}>
              {tc('actions.save', 'Speichern')}
            </Button>
          </DialogActions>
        </Dialog>

        <ConfirmDialog
          open={!!deleteId}
          title={t('offenePunkte.deleteTitle', 'Eintrag löschen?')}
          message={t('offenePunkte.deleteConfirm', 'Dieser Eintrag wird endgültig entfernt.')}
          onConfirm={handleDelete}
          onCancel={() => setDeleteId(null)}
        />

        <ConfirmDialog
          open={!!convertOp}
          title={
            convert?.target === 'anforderung' ? t('offenePunkte.convertAnforderungTitle', 'Als Anforderung übernehmen?')
            : convert?.target === 'risiko' ? t('offenePunkte.convertRisikoTitle', 'Als Risiko übernehmen?')
            : t('offenePunkte.convertTitle', 'In Arbeitspaket umwandeln?')
          }
          message={
            convert?.target === 'anforderung' ? t('offenePunkte.convertAnforderungConfirm', 'Aus diesem offenen Punkt wird eine neue Anforderung angelegt. Der offene Punkt bleibt bestehen.')
            : convert?.target === 'risiko' ? t('offenePunkte.convertRisikoConfirm', 'Aus diesem offenen Punkt wird ein neues Risiko angelegt. Der offene Punkt bleibt bestehen.')
            : t('offenePunkte.convertConfirm', 'Aus diesem offenen Punkt wird ein neues Arbeitspaket angelegt. Der offene Punkt bleibt bestehen.')
          }
          onConfirm={handleConvert}
          onCancel={() => setConvert(null)}
        />
      </Stack>
    </LocalizationProvider>
  );
}

/** Schnell-Anlegen oben: Titel + Kategorie, Enter legt an. Detail via Karte -> Dialog. */
function OpQuickAdd({ onCreate }: { onCreate: (titel: string, kategorie: string) => Promise<void> }) {
  const { t } = useTranslation('projects');
  const [titel, setTitel] = useState('');
  const [kategorie, setKategorie] = useState('');
  const [busy, setBusy] = useState(false);
  const canCreate = titel.trim().length > 0;

  const submit = async () => {
    if (!canCreate || busy) return;
    setBusy(true);
    try { await onCreate(titel, kategorie); setTitel(''); setKategorie(''); } finally { setBusy(false); }
  };

  return (
    <PmCard sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <TextField
          size="small" fullWidth
          placeholder={t('offenePunkte.titelPlaceholder', 'Neuen Punkt eintragen…')}
          value={titel}
          onChange={(e) => setTitel(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submit(); } }}
        />
        <Select size="small" displayEmpty value={kategorie} onChange={(e) => setKategorie(e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value="">{t('offenePunkte.kategorie', 'Kategorie')}</MenuItem>
          {KATEGORIEN.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
        </Select>
        <IconButton color="primary" onClick={() => void submit()} disabled={!canCreate || busy} aria-label={t('offenePunkte.add', 'Neuer Eintrag')}>
          <AddIcon />
        </IconButton>
      </Stack>
    </PmCard>
  );
}
